import { useEffect, useState } from 'react';
import './record-book.styles.scss';
import { Link } from 'react-router-dom';
import { RECORD_CATEGORIES, formatRecord } from '@shared/honors.js';
import { listenToDoc } from '@/utils/getDataFromDb';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

const GROUPS = [...new Set(RECORD_CATEGORIES.map(c => c.group))];

// The league's record book (summary/records): the best seasons and single
// games ever, ten per category. Entries from the latest season are marked.
const RecordBook = ({ league, gameLink, seasonLink, highlightId }) => {

    const [ book, setBook ] = useState(undefined);
    const [ group, setGroup ] = useState(GROUPS[0]);
    const [ category, setCategory ] = useState(RECORD_CATEGORIES[0].key);

    useEffect(() => {
        let unsub;
        listenToDoc({
            'path': `leagues/${league.id}/summary/records`,
            'callback': setBook,
            'handleListener': (u) => { unsub = u; },
        });
        return () => { if (unsub) unsub(); }
    }, [league.id])

    if (book === undefined) return <div className='center-col'><ThreeDotsLoader /></div>;

    const inGroup = RECORD_CATEGORIES.filter(c => c.group === group);
    const cat = inGroup.find(c => c.key === category) ?? inGroup[0];
    const rows = book?.categories?.[cat.key] ?? [];
    const latest = book?.years?.at(-1);
    const game = cat.kind === 'game';

    return (
        <div className='record-book'>
            <div className='g-card'>
                <h3>Record book</h3>
                <p className='meta'>
                    The best seasons and games in league history, updated when each season goes in the record books.
                    {cat.kind === 'avg' || cat.kind === 'pct' ? ' Per-game and shooting records need 60% of the team’s games' : ''}
                    {cat.kind === 'pct' ? ` and ${cat.minAtt} attempts a game.` : cat.kind === 'avg' ? '.' : ''}
                </p>
            </div>
            <div className='chips' role='tablist' aria-label='Record group'>
                {GROUPS.map(g => (
                    <button key={g} role='tab' aria-selected={g === group} className={`chip ${g === group ? 'active' : ''}`} onClick={() => setGroup(g)}>{g}</button>
                ))}
            </div>
            <div className='chips' role='tablist' aria-label='Record'>
                {inGroup.map(c => (
                    <button key={c.key} role='tab' aria-selected={c.key === cat.key} className={`chip ${c.key === cat.key ? 'active' : ''}`} onClick={() => setCategory(c.key)}>{c.label}</button>
                ))}
            </div>
            {rows.length === 0 ?
                <div className='g-card'><p className='meta'>Records start once the first season is over and put in the record books.</p></div> :
                <div className='g-card'>
                    <div className='table-scroll'>
                        <table className='g-table records-table'>
                            <thead>
                                <tr>
                                    <th className='num'>#</th>
                                    <th>Player</th>
                                    <th>Team</th>
                                    <th className='num'>Season</th>
                                    {game && <th>Game</th>}
                                    <th className='num'>{cat.label}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr key={`${r.playerId}-${r.year}`} className={r.teamId === highlightId ? 'highlight' : ''}>
                                        <td className='num'>{i + 1}</td>
                                        <td>
                                            <b>{r.name}</b>
                                            {r.year === latest && <span className='new-tag'>New</span>}
                                        </td>
                                        <td>{r.abbr}</td>
                                        <td className='num'><Link to={seasonLink(r.year)}>{r.year}</Link></td>
                                        {game && <td>{r.gameId ? <Link to={gameLink(r.gameId)}>Day {r.day}</Link> : '-'}</td>}
                                        <td className='num value'>{formatRecord(cat, r.value)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            }
        </div>
    )
}

export default RecordBook
